import Config from '@skin_config';

import { swarmLayers } from '@lbs/swarm-client';

import { GameTypeEnum, GameTypeViewEnum } from '@common/constants/enums/gameType';
import { getAuthData } from '@common/helpers/auth';
import { browserHistory } from '@common/helpers/browserHistory';
import ToastWrapper from '@common/helpers/toastWrapper';
import { swarmRequest } from '@common/helpers/util/requestMiddleware';
import { getSelectionsContextValue } from '@common/contexts/selections';
import { getBookingSelectionListSwarmRequest } from './terminalRequests/events';
import { BetslipCommunicator, BetslipEventsEnum } from '@betslip/communicator';

const getBookingDetailsV1 = (data) => {
  if (!data.data.selectionIds) {
    ToastWrapper('error', data.data);
    return;
  }

  return {
    eventIds: data.data.selectionIds,
    matchIds: data.data.MatchIds,
  };
};

const getBookingDetailsV2 = (data) => {
  if (!data.data?.bets) {
    ToastWrapper('error', data.data);
    return null;
  }

  return {
    betType: data.data.bets[0]?.BetType,
    eventIds: data.data.bets.flatMap((item) => (
      item.Selections.map((selection) => selection.SelectionId)
    )),
    matchIds: data.data.MatchIds,
    amount: data.data.bets[0]?.Amount, // replace when every single will have own stake +_+
  };
};

const checkBookId = async (data) => {
  const bookingDetails = Config.main.useBookingV2Api
    ? getBookingDetailsV2(data)
    : getBookingDetailsV1(data);

  const errorMessage = "There's not any active selection in this bet.";

  if (!bookingDetails) return

  if (!bookingDetails?.eventIds?.length && !bookingDetails?.matchIds?.length)
    return ToastWrapper('error', errorMessage);

  const fullselectionsTree = (
    await swarmRequest(
      getBookingSelectionListSwarmRequest(bookingDetails.eventIds, bookingDetails.matchIds),
    )
  ).data;

  const events = swarmLayers(fullselectionsTree);

  if (!events.length) {
    ToastWrapper('error', errorMessage);
    return;
  }

  const [{ boostedOddsGameIds }, setSelections] = getSelectionsContextValue();

  // Map each response selection by id so we can recover the per-leg flags
  // (IsBanker, BetBuilderMatchId) when stitching the swarm events back
  // together below. The v2 response carries BetBuilderMatchId on the legs
  // that belong to a bet builder card; we use that to re-tag them with
  // `isInternalBetBuilder: true` so the betslip reconstructs the card
  // (combined odds, single-card UI, etc.) just like when the user built
  // it interactively.
  const responseSelectionsById = (data.data?.bets || [])
    .flatMap((item) => item.Selections || [])
    .reduce((acc, sel) => {
      acc[sel.SelectionId] = sel;
      return acc;
    }, {});

  // Preserve the order the BOOKING call returned the selections in.
  // `bookingDetails.eventIds` came from the bets/selections sequence in the
  // bet-book payload, so re-sorting the swarm-resolved events to match
  // that index gives us the same leg order the user originally placed.
  // Without this, `events` is in swarm-tree traversal order (game > market
  // > event) which has no relation to how the legs were combined.
  const bookingOrderIndex = (bookingDetails.eventIds || []).reduce(
    (acc, id, idx) => {
      acc[id] = idx;
      return acc;
    },
    {},
  );
  const FAR_END = Number.MAX_SAFE_INTEGER;
  const orderedEvents = [...events].sort((a, b) => (
    (bookingOrderIndex[a.id] ?? FAR_END) - (bookingOrderIndex[b.id] ?? FAR_END)
  ));

  const selections = orderedEvents.reduce(
    (acc, selection) => {
      const responseSel = responseSelectionsById[selection.id];
      const isInternalBetBuilder = !!responseSel?.BetBuilderMatchId;
      // Keys are numeric-string ids — JS objects ALWAYS iterate
      // integer-like keys in ascending numeric order, regardless of
      // insertion order. So Object.values(events) downstream won't reflect
      // booking order. Sorting `orderedEvents` here still helps any code
      // path that iterates via the source array (e.g. the betslip's
      // rebuild flow that drops them into `events: selections` and then
      // applies its own ordering). If you need strict iteration order in
      // the slip too, add a sidecar `eventOrder: number[]` next to
      // `events` in the selections store.
      acc[selection.id] = {
        game: selection.game,
        market: selection.market,
        // Tag the event itself with isInternalBetBuilder so downstream
        // helpers (getBetBuilderGameIds / resolveBetType / betslip card
        // grouping) treat same-game BB legs as one combined card.
        event: isInternalBetBuilder
          ? { ...selection, isInternalBetBuilder: true }
          : selection,
        isBanker: responseSel?.IsBanker,
        isBoosted: boostedOddsGameIds[selection.game.id]?.some((item) => item.Id === selection.id),
      };
      return acc;
    },
    {},
  );

  setSelections((newValue) => ({
    ...newValue,
    events: selections,
    bookingAmount: bookingDetails.amount,
  }));

  if (bookingDetails.betType) {
    BetslipCommunicator.send(BetslipEventsEnum.ChangeBetType, {
      betType: bookingDetails.betType,
    });
  }
};

export const findByBookId = (id) => {
  const authData = getAuthData();
  const gameType = window.location.pathname.split('/')[1];
  const isSportPage = [
    GameTypeViewEnum[GameTypeEnum.Live],
    GameTypeViewEnum[GameTypeEnum.Prematch],
  ].includes(gameType);

  const apiUrl = Config.main.useBookingV2Api ? 'terminal_get_bet_by_bet_book_id_v2' : 'terminal_get_bet_by_bet_book_id';

  const params = { id, token: authData?.token };

  swarmRequest(params, apiUrl).then((data) => {
    if (!isSportPage) browserHistory.push('/betting/prematch');
    checkBookId(data);
  });

  window.barcodeBuffer = [];
};
